'use client';

import { useState, useMemo, useRef, useCallback } from 'react';
import _ from 'lodash';

// Components
import BeatLoader from '@src/components/atoms/beat-loader';

// Assets
import IMagnifier from '@public/svg/i-magnifier';
import IArrowRight1 from '@public/svg/i-arrow-right';

import { useGetListLocation } from '@src/apis/list-listing/query';
import { useDebounce } from '@src/hooks/useDebounce';
import useOutsideAlerter from '@src/hooks/useOutsideAlerter';

const highlightText = (text, highlight) => {
  if (!highlight) return text;
  if (!text) return '';

  const parts = text.split(new RegExp(`(${highlight})`, 'gi'));

  return (
    <span>
      {parts.map((part, i) => (
        <span
          key={i}
          style={
            part.toLowerCase() === highlight.toLowerCase()
              ? { fontFamily: 'Satoshi Bold' }
              : {}
          }
        >
          {part}
        </span>
      ))}
    </span>
  );
};

const Option = ({ search, currentVal, lastItemRef, slug, ...props }) => {
  return (
    <button
      ref={lastItemRef}
      className="w-full font-satoshi flex items-center justify-between py-4 text-[17px] border-b-[0.5px] border-dashed border-[#E6E7E8]"
      {...props}
    >
      {highlightText(currentVal, search)}

      <div className="flex items-center gap-2">
        <p className="bg-[#EBF0F7] rounded-[4px] px-2 py-0.5 font-satoshi text-[11px]/[15.6px] tracking-[0.11px] text-[#3268AE]">
          {slug}
        </p>
        <div className="w-4 h-4 flex items-center justify-center">
          <IArrowRight1 color="#686A68" width={16} height={16} />
        </div>
      </div>
    </button>
  );
};

const RenderOptions = ({
  data,
  value,
  onSelect,
  lastItemRef,
  isFetching,
  startTyping,
}) => {
  return (
    <div>
      <div
        id="scrollable-list"
        className="max-h-[420px] overflow-y-auto scrollbar-hide 
        dd2-xl:max-h-[calc(100vh-200px)]"
      >
        {data?.map((item, idx) => (
          <Option
            onClick={() => onSelect(item)}
            key={`${item.id}-${idx}`}
            slug={item.slug}
            search={value}
            currentVal={item.name}
          />
        ))}

        {/* Loading Sentinel positioned before the keyword option */}
        {data.length > 0 && (
          <div
            ref={lastItemRef}
            style={{ height: '5px', width: '100%', margin: '5px 0' }}
          />
        )}

        {!data.length && (
          <div
            onClick={() => onSelect({ name: value, slug: 'Keyword' })}
            className="flex items-center justify-between gap-2 border-b-[0.5px] border-dashed border-[#DEDFDE] py-4"
          >
            <p className="font-satoshi text-[17px]/[23.8px] tracking-[0.17px] text-[#040704]">
              {value}
            </p>

            <div className="flex items-center gap-2">
              <p className="bg-[#EBF0F7] rounded-[4px] px-2 py-0.5 font-satoshi text-[11px]/[15.6px] tracking-[0.11px] text-[#3268AE]">
                Kata Kunci
              </p>
              <div className="w-4 h-4 flex items-center justify-center">
                <IArrowRight1 width={16} height={16} />
              </div>
            </div>
          </div>
        )}

        {/* Loader */}
        {isFetching ? (
          <div>
            <div className="flex items-center gap-2 justify-between py-4">
              <p className="font-satoshi text-[14px]/[19.6px] text-[#040704]">
                Sedang mencari lokasi...
              </p>
              <BeatLoader />
            </div>
          </div>
        ) : null}

        {/* No Data */}
        {!isFetching && startTyping && data?.length === 0 ? (
          <div>
            <div className="flex items-start gap-4 justify-between py-4">
              <p className="font-satoshi text-[14px]/[19.6px] text-[#040704]">
                Lokasi tidak ditemukan. Coba ketik area/kota lain atau gunakan
                kata kunci.
              </p>
              <BeatLoader />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const ModalSearch = ({ location, onSelectLocation }) => {
  const ref = useRef(null);
  const observer = useRef();

  const [params, setParams] = useState({
    q: '',
    city_id: null,
    page: 1,
    _limit: 10,
  });
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [search, setSearch] = useState(location?.name || '');

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
    useGetListLocation({ params });

  const listListing = useMemo(() => {
    if (!data?.pages?.length) return [];

    return data.pages.map((d) => d?.data?.data ?? []).flat();
  }, [data]);

  const { debounceFn } = useDebounce({
    onChange: (newParams) => setParams(newParams),
  });

  const lastItemRef = useCallback(
    (node) => {
      if (isFetchingNextPage) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      });
      if (node) observer.current.observe(node);
    },
    [isFetchingNextPage, hasNextPage, fetchNextPage]
  );

  const handleSearch = (value) => {
    debounceFn({
      ...params,
      q: value,
      page: 1,
    });
  };

  useOutsideAlerter(ref, () => setIsOpenModal(false));
  return (
    <div>
      <div className="px-6 py-4">
        <p className="font-satoshi-bold text-[21px] leading-[25.2px] text-[#252526]">
          Di mana lokasi propertinya?
        </p>
        <p className="font-satoshi text-[14px] leading-[19.6px] text-[#686A68]">
          Ketik kota atau area agar kami bisa mencocokkan marketing terbaik.
        </p>
      </div>

      <div className="px-6 py-4" ref={ref}>
        <div className="flex items-center gap-4 rounded-[8px] border border-solid border-[#252526] h-[40px] px-[12px] bg-white">
          <IMagnifier />
          <input
            type="text"
            placeholder='cari "Setiabudi" atau "Jakarta Selatan"'
            className="w-full h-6"
            value={search}
            onFocus={() => setIsOpenModal(true)}
            onChange={(e) => {
              setSearch(e.target.value);
              handleSearch(e.target.value);
            }}
          />
        </div>

        <div className="mt-4">
          {isOpenModal && (
            <RenderOptions
              data={listListing}
              value={search}
              onSelect={(location) => {
                onSelectLocation(location);
                setIsOpenModal(false);
                setSearch(location?.name ?? '');
              }}
              lastItemRef={lastItemRef}
              isFetching={isFetching || isFetchingNextPage}
              startTyping={!!search}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ModalSearch;
